export type ManagementFilterSelectOption = {
  id: string;
  name: string;
};

/** Default gap between chips (MUI spacing 0.5 ≈ 4px). */
export const MANAGEMENT_FILTER_SELECT_CHIP_GAP_PX = 4;

/**
 * Closed-field label for Pattern A filters: empty = allLabel; else comma-joined names.
 */
export function formatManagementFilterSelectValue(
  selectedIds: string[],
  options: ManagementFilterSelectOption[],
  allLabel: string
): string {
  if (selectedIds.length === 0) {
    return allLabel;
  }

  const nameById = new Map(options.map(option => [option.id, option.name]));
  return selectedIds.map(id => nameById.get(id) ?? id).join(", ");
}

/**
 * Packs as many chips as fit in `availableWidth`. Reserves room for a +N chip when needed.
 * Returns how many leading chips to show (at least 1 when any exist).
 */
export function fitManagementFilterSelectChips(input: {
  chipWidths: number[];
  availableWidth: number;
  moreChipWidth: number;
  gap?: number;
}): { visibleCount: number; hiddenCount: number } {
  const { chipWidths, availableWidth, moreChipWidth } = input;
  const gap = input.gap ?? MANAGEMENT_FILTER_SELECT_CHIP_GAP_PX;

  if (chipWidths.length === 0) {
    return { visibleCount: 0, hiddenCount: 0 };
  }

  let allWidth = 0;
  for (let index = 0; index < chipWidths.length; index += 1) {
    allWidth += chipWidths[index] + (index > 0 ? gap : 0);
  }
  if (allWidth <= availableWidth) {
    return { visibleCount: chipWidths.length, hiddenCount: 0 };
  }

  let used = 0;
  let visibleCount = 0;
  for (let index = 0; index < chipWidths.length; index += 1) {
    const nextUsed = used + (visibleCount > 0 ? gap : 0) + chipWidths[index];
    const remainingAfter = chipWidths.length - (index + 1);
    const widthWithMore = remainingAfter > 0 ? nextUsed + gap + moreChipWidth : nextUsed;
    if (widthWithMore <= availableWidth) {
      used = nextUsed;
      visibleCount = index + 1;
      continue;
    }
    break;
  }

  if (visibleCount < 1) {
    visibleCount = 1;
  }

  return {
    visibleCount,
    hiddenCount: chipWidths.length - visibleCount
  };
}

/**
 * Slices selected ids into visible chips + hidden count for a known visible limit.
 */
export function splitManagementFilterSelectChips(
  selectedIds: string[],
  limit: number
): { visibleIds: string[]; hiddenCount: number } {
  if (limit >= selectedIds.length) {
    return { visibleIds: selectedIds, hiddenCount: 0 };
  }
  if (limit < 1) {
    return {
      visibleIds: selectedIds.slice(0, 1),
      hiddenCount: Math.max(0, selectedIds.length - 1)
    };
  }
  return {
    visibleIds: selectedIds.slice(0, limit),
    hiddenCount: selectedIds.length - limit
  };
}

/** Normalizes MUI Select onChange values (string or string[]) into id arrays. */
export function normalizeManagementFilterSelectIds(value: string | string[]): string[] {
  if (typeof value === "string") {
    return value === "" ? [] : [value];
  }
  return value.map(String);
}

export function resolveManagementFilterOptionLabel(
  id: string,
  options: ManagementFilterSelectOption[],
  formatOptionLabel?: (option: ManagementFilterSelectOption) => string
): string {
  const option = options.find(item => item.id === id);
  if (!option) {
    return id;
  }
  return formatOptionLabel ? formatOptionLabel(option) : option.name;
}

/**
 * Drill path location id: only when exactly one location is in scope.
 */
export function getSingleLocationIdForDrill(locationIds: string[]): string | null {
  if (locationIds.length !== 1) {
    return null;
  }
  return locationIds[0] ?? null;
}
